import { z } from 'zod';
import { gatedStepsSchema } from '../constants/call-playbook';
import { OUTBOUND_CALL_OUTCOME_VALUES } from '../constants/outbound-call-outcome';
import { outreachSignalSchema } from '../constants/outreach';

/**
 * What one call produced, as one typed object (ADR-318, P13).
 *
 * Built by the voice worker at teardown — or by the API's dialer for a call
 * that never connected — sent to `POST /internal/interactions/:id/complete`,
 * validated there and stored in `interactions.call_result`.
 *
 * WHY IT EXISTS. Before it, a call's result was spread over four places, none of
 * them a contract: `outcome_category`, free-string `structuredOutput` entries
 * matched by literal, log events, and the workflow engine's own
 * `WorkflowRunOutcome`, which never left the worker. Outreach now reads this
 * object and nothing else — except for SUCCESS, which still reads the system of
 * record (the appointment row), because a model saying "booked" is not evidence
 * (ADR-154).
 *
 * VERSIONED. `v` lets the completion hook tell a worker that sends this from
 * one deployed before it existed (which sends `outcome` + `structuredOutput`
 * only), and lets a later shape coexist with this one.
 */

/** `interactions.outcome_category`'s vocabulary, as a schema. */
export const outboundCallOutcomeSchema = z.enum(OUTBOUND_CALL_OUTCOME_VALUES);

/**
 * Why the workflow walk stopped.
 *
 * MIRRORS `WorkflowStop` in `packages/agent-core/src/v2/runner.ts`, which is
 * where the values are defined and documented. Restated because `agent-core`
 * does not depend on this package; `apps/livekit` depends on both and checks
 * the two agree at compile time (`call-result-contract.test.ts`), so adding a
 * stop reason there without adding it here breaks the build rather than every
 * completion that reports it.
 */
export const WORKFLOW_STOP_VALUES = [
  'completed',
  'node_incomplete',
  'closed_early',
  'dropped',
  'evicted',
  'step_cap',
  'supervisor_stalled',
] as const;
export const workflowStopSchema = z.enum(WORKFLOW_STOP_VALUES);
export type WorkflowStopValue = z.infer<typeof workflowStopSchema>;

/**
 * Where the workflow a call ran came from.
 *
 *   pinned            a campaign step's pinned version (P1, P2)
 *   published         the org's published workflow for the call's purpose
 *   builtin           the code-defined workflow, because nothing was published
 *                     for the call's purpose or routing by purpose is off
 *   builtin_fallback  the code-defined workflow, run because a pinned or
 *                     published one could not be loaded (P12) — countable here
 *   snapshot          a builder test's frozen definition
 *   section_test      a v2 section test (ADR-265/266)
 */
export const CALL_WORKFLOW_SOURCES = [
  'pinned',
  'published',
  'builtin',
  'builtin_fallback',
  'snapshot',
  'section_test',
] as const;

export type CallWorkflowSource = (typeof CALL_WORKFLOW_SOURCES)[number];

export const callResultSchema = z.object({
  v: z.literal(1),
  interactionId: z.string().uuid(),
  /** Same value written to `interactions.outcome_category`. */
  disposition: outboundCallOutcomeSchema,
  /** We spoke with the person we meant to reach. False for voicemail, no answer, wrong number. */
  reached: z.boolean(),
  /** Null when the call never connected. */
  workflow: z
    .object({
      source: z.enum(CALL_WORKFLOW_SOURCES),
      /** The `workflow_defn_versions` row that ran; null for a code-defined one. */
      versionId: z.string().uuid().nullable(),
      key: z.string(),
      version: z.number().int().nullable(),
      stoppedBecause: workflowStopSchema,
      stoppedAt: z.string().nullable(),
      /** Steps that ran, in order, each with the outcome it ended on. */
      path: z.array(z.object({ nodeId: z.string(), outcome: z.string().nullable() })),
      /**
       * The playbook steps this call's facts gated out (ADR-362,
       * `gatedStepsSchema`): reached, skipped by their own gates, never run.
       * The post-call evaluator does not expect them. Absent from a result sent
       * by a worker deployed before it, which is then judged as before.
       */
      gatedSteps: gatedStepsSchema.optional(),
    })
    .nullable(),
  /** From the fact ledger. Evidence for reporting; success still reads the appointment row. */
  booking: z.object({ appointmentId: z.string() }).nullable(),
  /** `callback_requested` · `voicemail_left` · `optout_requested`. */
  signals: z.array(outreachSignalSchema),
  timings: z.object({
    // Any offset, not only `Z`: a `+00:00` timestamp must not void the result.
    answeredAt: z.string().datetime({ offset: true }).nullable(),
    endedAt: z.string().datetime({ offset: true }),
    talkSeconds: z.number().int().nonnegative(),
  }),
});
export type CallResult = z.infer<typeof callResultSchema>;
